/**
 * lib/useDevices.ts — ONE shared answer to "what sessions does this account
 * have, and is session management even switched on?".
 *
 * ── WHY A SHARED STORE AND NOT A useState PER SCREEN ──────────────────────
 * Three surfaces need this at once: the desktop account menu (should the
 * "Devices & sessions" item exist at all?), the mobile Profile row (same
 * question, plus "2 of 3 signed in"), and the panel itself. A per-component
 * fetch would mean three `GET /me/devices` on one page load and three
 * independent ideas of whether the feature is available. One module-level
 * store, one request, one truth — the same shape `lib/identity.ts` uses for
 * `/me`.
 *
 * ── THE FLAG-OFF CASE IS THE DEFAULT CASE, NOT AN ERROR ───────────────────
 * `GET /me/devices` 404s while `DEVICE_LIMIT_LIVE` is unset — today's prod.
 * That resolves to `status:'unavailable'`, which means: render NOTHING. No
 * nav row, no error, no empty state. A feature that is off must be invisible,
 * not broken. `unavailable` is also sticky — we never re-poll a 404, so an
 * unarmed deploy costs exactly one request per session.
 *
 * ── NO DEAD ENDS ──────────────────────────────────────────────────────────
 * Every failure resolves to a RENDERABLE state with an action: `error` carries
 * `reload()`, `unavailable` carries an explanation, and `revoke()` reports
 * `gone` separately from `error` so a stale row can always be removed.
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react'
import {
  listDevices, revokeDevice as revokeOne, type DeviceSummary, type RevokeResult,
} from './deviceSession'
import { useIdentity } from './identity'

export interface DevicesState {
  /** `unavailable` = the feature is off (404). Render nothing at all. */
  status: 'idle' | 'loading' | 'ready' | 'error' | 'unavailable'
  devices: DeviceSummary[]
  limit: number
  exempt: boolean
  currentDeviceId: string | null
  error: string | null
}

const EMPTY: DevicesState = {
  status: 'idle', devices: [], limit: 3, exempt: false, currentDeviceId: null, error: null,
}

let snapshot: DevicesState = EMPTY
let loadedUid: string | null = null
let inFlight = false
/** Bumped by every load and every reset; a response whose token is stale is
 *  discarded. See `loadDevices` for the race it closes. */
let requestSeq = 0
const subs = new Set<() => void>()

function emit(next: DevicesState): void {
  snapshot = next
  subs.forEach((f) => { try { f() } catch { /* one bad subscriber must not stop the rest */ } })
}

export function getDevicesSnapshot(): DevicesState { return snapshot }
export function subscribeDevices(cb: () => void): () => void {
  subs.add(cb)
  return () => { subs.delete(cb) }
}

/** Drop everything. Called when the signed-in user changes — one person's
 *  session list must never be readable by the next person on a shared machine. */
export function resetDevices(): void {
  loadedUid = null
  inFlight = false
  requestSeq += 1        // an in-flight response must not repopulate the store
  emit(EMPTY)
}

export function loadDevices(uid: string, force = false): void {
  const sameUser = loadedUid === uid
  if (sameUser && !force) {
    // `unavailable` is a permanent answer for this session: the flag is not
    // going to flip between two mounts, and re-asking a 404 on every
    // navigation is noise on a route that will 404 for months.
    if (snapshot.status === 'unavailable') return
    if (snapshot.status === 'ready' || snapshot.status === 'loading') return
    if (inFlight) return
  }
  /**
   * ── A USER CHANGE ALWAYS WINS, EVEN MID-FLIGHT ──────────────────────────
   * The `inFlight` guard used to apply to EVERY caller, so switching accounts
   * while `GET /me/devices` was still open dropped the new user's load on the
   * floor — `loadedUid` stayed on the old user, and when the old response
   * landed it passed its own `loadedUid === uid` check and published ONE
   * PERSON'S SESSION LIST INTO THE NEXT PERSON'S SCREEN. Device labels,
   * platforms and last-seen times, on a shared trading-desk machine, with a
   * "sign out" button beside each one. And because nothing then re-asked, the
   * new user's real list never loaded at all.
   *
   * So a different uid always starts a fresh request, the previous user's rows
   * are dropped from the store in the same tick (never shown to the new user
   * even for a frame), and the sequence token below makes the in-flight
   * response a no-op when it eventually resolves.
   */
  const seq = ++requestSeq
  loadedUid = uid
  inFlight = true
  emit(sameUser
    ? { ...snapshot, status: 'loading', error: null }
    : { ...EMPTY, status: 'loading' })
  void listDevices().then((r) => {
    // Not `loadedUid !== uid`: two loads for the SAME uid (a `force` reload
    // over a slow first request) also have to resolve last-writer-wins.
    if (seq !== requestSeq) return
    inFlight = false
    if (r.kind === 'off') { emit({ ...EMPTY, status: 'unavailable' }); return }
    if (r.kind === 'error') { emit({ ...EMPTY, status: 'error', error: r.message }); return }
    emit({
      status: 'ready',
      devices: r.devices,
      limit: r.limit,
      exempt: r.exempt,
      currentDeviceId: r.currentDeviceId,
      error: null,
    })
  })
}

/**
 * Apply the outcome of a revoke to the store without a refetch, so the row
 * leaves under the user's finger rather than after a round trip.
 * `gone` removes the row too — see `revokeDevice`'s contract.
 */
export function applyRevoke(deviceId: string, result: RevokeResult): void {
  if (result.kind === 'error') return
  if (result.kind === 'ok' && result.devices.length > 0) {
    emit({ ...snapshot, devices: result.devices, limit: result.limit, status: 'ready' })
    return
  }
  emit({ ...snapshot, devices: snapshot.devices.filter((d) => d.deviceId !== deviceId) })
}

export interface DevicesApi extends DevicesState {
  reload: () => void
  revoke: (deviceId: string) => Promise<RevokeResult>
  /** How many of `limit` are in use. `exempt` accounts have no limit at all. */
  used: number
  freeSlots: number
}

export function useDevices(): DevicesApi {
  const id = useIdentity()
  const snap = useSyncExternalStore(subscribeDevices, getDevicesSnapshot, () => snapshot)

  useEffect(() => {
    // In an effect, never in render: `loadDevices` publishes to an external
    // store, and publishing during render is a cross-component setState.
    if (id.signedIn && id.uid) loadDevices(id.uid)
    else if (id.status === 'anon' || id.status === 'unavailable') resetDevices()
  }, [id.signedIn, id.uid, id.status])

  const reload = useCallback(() => { if (id.uid) loadDevices(id.uid, true) }, [id.uid])

  const revoke = useCallback(async (deviceId: string): Promise<RevokeResult> => {
    const r = await revokeOne(deviceId)
    applyRevoke(deviceId, r)
    return r
  }, [])

  const used = snap.devices.length
  return {
    ...snap,
    reload,
    revoke,
    used,
    freeSlots: snap.exempt ? 0 : Math.max(0, snap.limit - used),
  }
}
